<template>
  <LoadingPage v-if="isLoading && !showSampleContractsModal"></LoadingPage>
  <div class="proposal-container">
    <!-- Mobile drawer backdrop -->
    <div v-if="isMobileDrawerOpen" class="mobile-drawer-backdrop" @click="closeMobileDrawer"></div>
    <aside class="sidebar" :class="{ 'collapsed': isSidebarCollapsed, 'mobile-drawer-open': isMobileDrawerOpen }">
      <div class="sidebar-logo">
        <img 
          :src="isSidebarCollapsed ? collapsedLogo : fullLogo" 
          :alt="isSidebarCollapsed ? 'Vertex Logo' : 'Vertex Cyber Security Logo'" 
          :class="['logo', { 'collapsed-logo': isSidebarCollapsed }]">
        <hr v-if="!isSidebarCollapsed" />
      </div>
      <button @click="toggleSidebar" class="collapse-btn">
        <span v-if="isSidebarCollapsed"><i class="fa-solid fa-chevron-right"></i></span>
        <span v-else><i class="fa-solid fa-chevron-left"></i></span>
      </button>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Organisation</h3>
        <p>{{ this.proposal.Lead.auth_organisation_name || this.proposal.Lead.organisation_name || "No Organisation Name" }}</p>
      </div>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Organisation {{ businessNumberShortLabel }}</h3>
        <p>{{ this.proposal.Lead.business_number || this.proposal.Lead.abn || ("No Registered " + businessNumberShortLabel) }}</p>
      </div>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Client Lead Name</h3>
        <p>{{ (this.proposal.Lead.first_name + " " + this.proposal.Lead.last_name) || "No Lead Name" }}</p>
      </div>
      <div class="sidebar-section" v-if="visibleSharedUsers.length > 0">
        <h3 v-if="!isSidebarCollapsed">Shared With</h3>
        <div class="shared-user-list" v-if="!isSidebarCollapsed">
          <div v-for="user in visibleSharedUsers" :key="user.email" class="shared-user-item">
            <span class="user-email" :title="user.email">{{ user.email }}</span>
            <span class="user-role">{{ user.share_type }}</span>
          </div>
        </div>
      </div>
      <div class="sidebar-section" v-if="!isSidebarCollapsed">
        <h3>Charity Status</h3>
        <div v-if="this.proposal.Lead.is_charity" class="status-indicator active">Registered Charity</div>
        <div v-else class="status-indicator not-active">Non-Charity</div>
      </div>
    </aside>

    <main class="main-content" :class="{ 'expanded': isSidebarCollapsed }">
      <header class="proposal-header">
        <button class="mobile-menu-btn" @click="toggleMobileDrawer">
          <i class="fa-solid fa-bars"></i>
        </button>
        <div class="header-text">
          <h1>{{ this.proposal.proposal_title }}</h1>
          <button 
            v-if="filteredProposalItems.length > 0 || acceptedProposalItems.length > 0" 
            class="jump-to-pricing-btn" 
            @click="scrollToPricing"
          >
            <i class="fa-solid fa-arrow-down"></i> Jump to Pricing
          </button>
        </div>
        <div class="header-actions">
          <button class="button secondary" @click="downloadProposalPdf" :hidden="IsHidden" :disabled="!proposal.proposal_title">Download</button>
          <button v-if="!isProposalExpired && canAcceptProposal && !this.proposal.is_accepted" class="button primary" @click="openProceedWizard" :disabled="!canAcceptProposal">Proceed to Contract</button>
          <span v-if="canAcceptProposal && this.proposal.is_accepted && currentProposalStatus === 'partially_accepted'" class="button accepted-proposal partially-accepted" style="cursor: default;">Partially Accepted</span>
          <span v-else-if="canAcceptProposal && this.proposal.is_accepted" class="button accepted-proposal" style="cursor: default;">Accepted</span>
          
          <!-- Repositioned action buttons for mobile (≤ 768px) -->
          <div v-if="!this.proposal.is_accepted && !isStaticView" class="mobile-top-actions">
             <button 
              :disabled="!canShareProposal" 
              :class="{ 'teal': canShareProposal, 'grey': !canShareProposal }" 
              class="button share" 
              @click="openShareModal">
              Share
            </button>
            <button 
              :disabled="isProposalExpired" 
              :class="{ 'green': !isProposalExpired, 'grey': isProposalExpired || isDeleted }" 
              class="button book-call" 
              @click="openMeetModal">
              Book Call
            </button>
            <button 
              :disabled="isProposalExpired" 
              :class="{ 'orange': !isProposalExpired, 'grey': isProposalExpired || isDeleted }" 
              class="button ask-question" 
              @click="openAskQuestionModal"> 
              Ask Question
            </button>
          </div>
        </div>
      </header>

      <div>
        <template v-for="(section, sectionIndex) in orderedSections" :key="section">

          <!-- CoverPage -->
          <div v-if="section === 'CoverPage' && hasContent('cover_page_content')">
            <br v-if="sectionIndex > 0">
            <section class="proposal-details">
              <div class="proposal-introduction">
                <div v-html="proposal.proposal_section_content.cover_page_content"></div>
              </div>
            </section>
          </div>

          <!-- ProductService -->
          <div v-else-if="section === 'ProductService' && hasContent('product_service_content')">
            <br>
            <section class="proposal-details">
              <div class="product-services">
                <p style="font-family: 'Roboto', sans-serif; font-size: 18px; color: #00a0c1; margin-bottom: 12px;"><strong>Proposed Solution</strong></p>
                <div v-html="proposal.proposal_section_content.product_service_content"></div>
              </div>
            </section>
          </div>

          <!-- PricingTable -->
          <div v-else-if="section === 'PricingTable' && (filteredProposalItems.length > 0 || acceptedProposalItems.length > 0)">
            <br>
            <section class="proposal-details" id="pricing_table_section">

              <div v-if="proposal.proposal_section_content && proposal.proposal_section_content.pricing_table_introduction_content && !isPlaceholderOrEmptyHTML(proposal.proposal_section_content.pricing_table_introduction_content)" 
                   class="pricing-intro-content" 
                   v-html="proposal.proposal_section_content.pricing_table_introduction_content"
                   style="margin-bottom: 20px; line-height: 1.6;">
              </div>

              <div v-if="!proposal.is_accepted">
                
                <div class="tab-wrapper" v-if="filteredProposalItems.length > 1">
                  <p style="font-family: 'Roboto', sans-serif; font-size: 18px; color: #00a0c1; margin-bottom: 12px;"><strong>Product Pricing</strong></p>
                  <ul class="nav nav-tabs" role="tablist">
                    <li v-for="(pi, index) in filteredProposalItems" :key="index" class="nav-item" role="presentation">
                      <button class="nav-link" :class="{ 'active': pi.option_index == 1 }" :id="pi.option_index + '-tab'" data-bs-toggle="tab" :data-bs-target="'#' + pi.option_index + '-panel'" type="button" role="tab" :aria-controls="pi.option_index + '-panel'" :aria-selected="(pi.option_index == 1) ? 'true' : 'false'">
                        {{ (pi.option_index == 1) ? 'Main' : 'Option ' + toOptionName(pi.option_index) }}
                      </button>
                    </li>
                  </ul>
                  <div class="tab-content">
                    <div v-for="(pi, index) in filteredProposalItems" :key="index" class="tab-pane fade" :class="{ 'show active': pi.option_index == 1 }" :id="pi.option_index + '-panel'" role="tabpanel" :aria-labelledby="pi.option_index + '-tab'">
                      <table class="product-table">
                         <thead>
                          <tr>
                            <th style="width: 22%;">Product</th>
                            <th style="width: 35%;">Description</th>
                            <th style="width: 6%; text-align: center;">Quantity</th>
                            <th style="width: 10%;">Price</th>
                            <th style="width: 12%;">Pricing Plan Type</th>
                            <th v-if="proposal.show_discount_calculation">Item Discount</th>
                            <th v-if="proposal.show_discount_calculation">Total Discount</th>
                            <th style="width: 12%;">Total AUD</th>
                          </tr>
                        </thead>
                        <tbody>
                          <template v-for="(item, itemIndex) in pi.productList" :key="itemIndex">
                            <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                              <td :colspan="(proposal.show_discount_calculation ? 8 : 6)" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                                {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                              </td>
                            </tr>
                            <tr v-else :class="{'child-row': item.isChild}">
                              <td :class="{'child-item-cell': item.isChild}">
                                <div :style="item.isChild ? 'padding-left: 15px; display: flex; align-items: center;' : ''">
                                  <i v-if="item.isChild" class="fa-solid fa-play" style="color: #00a0c1; font-size: 0.65em; margin-right: 8px;"></i>
                                  <strong v-if="!item.isChild" class="parent-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong>
                                  <span v-else class="child-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</span>
                                </div>
                              </td>
                              <td :style="item.isChild ? 'padding-left: 20px; text-align: left;' : 'text-align: left;'">
                                <div class="desc-content" v-html="item.description"></div>
                                <div v-if="item.remarks" class="remarks-content">
                                   <span class="remarks-badge">Note</span> <span v-html="item.remarks"></span>
                                </div>
                              </td>
                              <td style="text-align: center;"><span>{{ item.quantity }}</span></td>
                              <td>
                                <template v-if="!item.isChild || parseFloat(item.price) > 0">
                                  <span v-if="!proposal.show_discount_calculation">{{ formatCurrency(item.price - item.discount) }}</span>
                                  <span v-else>{{ formatCurrency(item.price) }}</span>
                                </template>
                                <span v-else class="badge-included">Included</span>
                              </td>
                              <td>
                                 <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ item.pricingPlanType || '-' }}</span>
                                 <span v-else class="badge-included">Included</span>
                              </td>
                              <td v-if="proposal.show_discount_calculation">
                                <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount) }}</span>
                                <span v-else>-</span>
                              </td>
                              <td v-if="proposal.show_discount_calculation">
                                <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount * item.quantity) }}</span>
                                <span v-else>-</span>
                              </td>
                              <td>
                                <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(calculateTotal(item)) }}</span>
                                <span v-else class="badge-included">Included</span>
                              </td>
                            </tr>
                          </template>
                          <tr class="total-row">
                            <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" class="total-label">Subtotal</td>
                            <td class="total-amount">{{ formatCurrency(calculateOptionSubTotalPrice(pi.productList)) }}</td>
                          </tr>
                          <tr class="total-row">
                            <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" title="Goods and Services Tax" class="total-label">GST (10%)</td>
                            <td class="total-amount">{{ formatCurrency(calculateOptionGST(pi.productList)) }}</td>
                          </tr>
                          <tr class="total-row">
                            <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" class="total-label">Total AUD</td>
                            <td class="total-amount">{{ formatCurrency(calculateOptionTotalPrice(pi.productList)) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div class="simple-pricing-wrapper" v-else-if="filteredProposalItems.length > 0">
                  <p style="font-family: 'Roboto', sans-serif; font-size: 18px; color: #00a0c1; margin-bottom: 12px;"><strong>Product Pricing</strong></p>
                  <table class="product-table">
                    <thead>
                      <tr>
                        <th style="width: 22%;">Product</th>
                        <th style="width: 35%;">Description</th>
                        <th style="width: 6%; text-align: center;">Quantity</th>
                        <th style="width: 10%;">Price</th>
                        <th style="width: 12%;">Pricing Plan Type</th>
                        <th v-if="proposal.show_discount_calculation">Item Discount</th>
                        <th v-if="proposal.show_discount_calculation">Total Discount</th>
                        <th style="width: 12%;">Total AUD</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="(item, itemIndex) in filteredProposalItems[0].productList" :key="itemIndex">
                        <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                          <td :colspan="(proposal.show_discount_calculation ? 8 : 6)" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                            {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                          </td>
                        </tr>
                        <tr v-else :class="{'child-row': item.isChild}">
                          <td :class="{'child-item-cell': item.isChild}">
                            <div :style="item.isChild ? 'padding-left: 15px; display: flex; align-items: center;' : ''">
                              <i v-if="item.isChild" class="fa-solid fa-play" style="color: #00a0c1; font-size: 0.65em; margin-right: 8px;"></i>
                              <strong v-if="!item.isChild" class="parent-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong>
                              <span v-else class="child-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</span>
                            </div>
                          </td>
                          <td :style="item.isChild ? 'padding-left: 20px; text-align: left;' : 'text-align: left;'">
                            <div class="desc-content" v-html="item.description"></div>
                            <div v-if="item.remarks" class="remarks-content">
                               <span class="remarks-badge">Note</span> <span v-html="item.remarks"></span>
                            </div>
                          </td>
                          <td style="text-align: center;"><span>{{ item.quantity }}</span></td>
                          <td>
                            <template v-if="!item.isChild || parseFloat(item.price) > 0">
                              <span v-if="!proposal.show_discount_calculation">{{ formatCurrency(item.price - item.discount) }}</span>
                              <span v-else>{{ formatCurrency(item.price) }}</span>
                            </template>
                            <span v-else class="badge-included">Included</span>
                          </td>
                          <td>
                             <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ item.pricingPlanType || '-' }}</span>
                             <span v-else class="badge-included">Included</span>
                          </td>
                          <td v-if="proposal.show_discount_calculation">
                            <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount) }}</span>
                            <span v-else>-</span>
                          </td>
                          <td v-if="proposal.show_discount_calculation">
                            <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount * item.quantity) }}</span>
                            <span v-else>-</span>
                          </td>
                          <td>
                            <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(calculateTotal(item)) }}</span>
                            <span v-else class="badge-included">Included</span>
                          </td>
                        </tr>
                      </template>
                      <tr class="total-row">
                        <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" class="total-label">Subtotal</td>
                        <td class="total-amount">{{ formatCurrency(calculateOptionSubTotalPrice(filteredProposalItems[0].productList)) }}</td>
                      </tr>
                      <tr class="total-row">
                        <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" title="Goods and Services Tax" class="total-label">GST (10%)</td>
                        <td class="total-amount">{{ formatCurrency(calculateOptionGST(filteredProposalItems[0].productList)) }}</td>
                      </tr>
                      <tr class="total-row">
                        <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" class="total-label">Total AUD</td>
                        <td class="total-amount">{{ formatCurrency(calculateOptionTotalPrice(filteredProposalItems[0].productList)) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>

              <div v-else-if="proposal.is_accepted && acceptedProposalItems.length > 0" class="simple-pricing-wrapper">
                <p style="font-family: 'Roboto', sans-serif; font-size: 18px; color: #00a0c1; margin-bottom: 12px;"><strong>Accepted Products &amp; Services</strong></p>
                <table class="product-table">
                  <thead>
                    <tr>
                      <th style="width: 22%;">Product</th>
                      <th style="width: 35%;">Description</th>
                      <th style="width: 6%; text-align: center;">Quantity</th>
                      <th style="width: 10%;">Price</th>
                      <th style="width: 12%;">Pricing Plan Type</th>
                      <th v-if="proposal.show_discount_calculation">Item Discount</th>
                      <th v-if="proposal.show_discount_calculation">Total Discount</th>
                      <th style="width: 12%;">Total AUD</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="(item, itemIndex) in acceptedProposalItems[0].productList" :key="itemIndex">
                      <tr v-if="item.is_heading || item.IsHeading" class="heading-row" style="background-color: #f1f5f9;">
                        <td :colspan="(proposal.show_discount_calculation ? 8 : 6)" style="padding: 10px 16px; font-weight: bold; color: #1e293b; border-top: 1px solid #cbd5e1; border-bottom: 1px solid #cbd5e1; text-align: left; vertical-align: middle;">
                          {{ item.product_name || item.Product?.display_name || item.Product?.name || 'Section Heading' }}
                        </td>
                      </tr>
                      <tr v-else :class="{'child-row': item.isChild}">
                        <td :class="{'child-item-cell': item.isChild}">
                          <div :style="item.isChild ? 'padding-left: 15px; display: flex; align-items: center;' : ''">
                            <i v-if="item.isChild" class="fa-solid fa-play" style="color: #00a0c1; font-size: 0.65em; margin-right: 8px;"></i>
                            <strong v-if="!item.isChild" class="parent-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</strong>
                            <span v-else class="child-product-title">{{ item.product_name || item.Product?.display_name || item.Product?.name || 'Unknown Product' }}</span>
                          </div>
                        </td>
                        <td :style="item.isChild ? 'padding-left: 20px; text-align: left;' : 'text-align: left;'">
                          <div class="desc-content" v-html="item.description"></div>
                          <div v-if="item.remarks" class="remarks-content">
                             <span class="remarks-badge">Note</span> <span v-html="item.remarks"></span>
                          </div>
                        </td>
                        <td style="text-align: center;"><span>{{ item.quantity }}</span></td>
                        <td>
                          <template v-if="!item.isChild || parseFloat(item.price) > 0">
                            <span v-if="!proposal.show_discount_calculation">{{ formatCurrency(item.price - item.discount) }}</span>
                            <span v-else>{{ formatCurrency(item.price) }}</span>
                          </template>
                          <span v-else class="badge-included">Included</span>
                        </td>
                        <td>
                           <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ item.pricingPlanType || '-' }}</span>
                           <span v-else class="badge-included">Included</span>
                        </td>
                        <td v-if="proposal.show_discount_calculation">
                          <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount) }}</span>
                          <span v-else>-</span>
                        </td>
                        <td v-if="proposal.show_discount_calculation">
                          <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(item.discount * item.quantity) }}</span>
                          <span v-else>-</span>
                        </td>
                        <td>
                          <span v-if="!item.isChild || parseFloat(item.price) > 0">{{ formatCurrency(calculateTotal(item)) }}</span>
                          <span v-else class="badge-included">Included</span>
                        </td>
                      </tr>
                    </template>
                    <tr class="total-row">
                      <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" class="total-label">Subtotal</td>
                      <td class="total-amount">{{ formatCurrency(calculateOptionSubTotalPrice(acceptedProposalItems[0].productList)) }}</td>
                    </tr>
                    <tr class="total-row">
                      <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" title="Goods and Services Tax" class="total-label">GST (10%)</td>
                      <td class="total-amount">{{ formatCurrency(calculateOptionGST(acceptedProposalItems[0].productList)) }}</td>
                    </tr>
                    <tr class="total-row">
                      <td :colspan="(proposal.show_discount_calculation ? 7 : 5)" class="total-label">Total AUD</td>
                      <td class="total-amount">{{ formatCurrency(calculateOptionTotalPrice(acceptedProposalItems[0].productList)) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="proposal.proposal_section_content && proposal.proposal_section_content.pricing_table_notes_content && !isPlaceholderOrEmptyHTML(proposal.proposal_section_content.pricing_table_notes_content)" 
                   class="pricing-notes-content" 
                   v-html="proposal.proposal_section_content.pricing_table_notes_content"
                   style="margin-top: 25px; margin-bottom: 15px; line-height: 1.6;">
              </div>

              <div v-if="!proposal.is_accepted && !isProposalExpired && canAcceptProposal" class="additional-actions primary" style="margin-top: 25px;">
                <button class="button primary" @click="openProceedWizard" :disabled="!canAcceptProposal">
                  Proceed to Contract
                </button>
              </div>

              <div v-if="proposal.is_accepted && canAcceptAdditionalItems && canAcceptProposal" class="additional-actions primary" style="margin-top: 25px;">
                <button class="button primary" @click="openProceedWizard">
                  Accept Additional Product & Services
                </button>
              </div>

            </section>
          </div>

          <!-- ImageGrid -->
          <div v-else-if="section === 'ImageGrid' && hasContent('image_grid_content')">
            <br>
            <section class="proposal-details" id="image_grid_section">
              <div class="content-blocks image-grid-section" v-html="proposal.proposal_section_content.image_grid_content"></div>
            </section>
          </div>

          <!-- Testimonials -->
          <div v-else-if="section === 'Testimonials' && clientTestimonials && clientTestimonials.length > 0">
            <br>
            <div class="testimonial-section">
              <carousel :testimonialsData="clientTestimonials"></carousel>
            </div>
          </div>

          <!-- SocialImpact -->
          <div v-else-if="section === 'SocialImpact' && hasContent('content_social_impact')">
            <br>
            <section class="proposal-details" id="content_blocks_section_social_impact">
              <div class="content-blocks" v-html="proposal.proposal_section_content.content_social_impact"></div>
            </section>
          </div>

          <!-- Commitment -->
          <div v-else-if="section === 'Commitment' && hasContent('content_commitment')">
            <br>
            <section class="proposal-details" id="content_blocks_section_commitment">
              <div class="content-blocks" v-html="proposal.proposal_section_content.content_commitment"></div>
            </section>
          </div>

          <!-- WHS -->
          <div v-else-if="section === 'WHS' && hasContent('content_whs_responsibilities')">
            <br>
            <section class="proposal-details" id="content_blocks_section_whs">
              <div class="content-blocks" v-html="proposal.proposal_section_content.content_whs_responsibilities"></div>
            </section>
          </div>

          <!-- TermsConditions -->
          <div v-else-if="section === 'TermsConditions' && hasContent('content_terms_conditions')">
            <br>
            <section class="proposal-details" id="content_blocks_section_terms">
              <div class="content-blocks" v-html="proposal.proposal_section_content.content_terms_conditions"></div>
            </section>
          </div>

          <!-- NextSteps -->
          <div v-else-if="section === 'NextSteps' && hasContent('content_next_steps')">
            <br>
            <section class="proposal-details" id="content_blocks_section_next_steps">
              <div class="content-blocks" v-html="proposal.proposal_section_content.content_next_steps"></div>
            </section>
          </div>

        </template>
        <div class="bottom-spacer" style="height: 40px;"></div>
      </div>
    </main>

    <aside class="right-sidebar">
      <div v-if="!isStaticView" class="top-actions">
        <button 
          v-if="canAcceptProposal && (!isProposalExpired && !proposal.is_accepted || canAcceptAdditionalItems)"
          :disabled="!canAcceptProposal"
          class="button proceed-to-contract" 
          @click="openProceedWizard">
          Proceed to Contract
        </button>
        <button 
          :disabled="!canShareProposal || this.proposal.is_accepted" 
          :class="{ 'teal': canShareProposal && !this.proposal.is_accepted, 'grey': !canShareProposal || this.proposal.is_accepted }" 
          class="button share" 
          @click="openShareModal">
          Share
        </button>
        
        <button 
          :disabled="isProposalExpired || this.proposal.is_accepted" 
          :class="{ 'green': !isProposalExpired && !this.proposal.is_accepted, 'grey': isProposalExpired || isDeleted || this.proposal.is_accepted }" 
          class="button book-call" 
          @click="openMeetModal">
          Book Call
        </button>

        <button 
          :disabled="isProposalExpired || this.proposal.is_accepted" 
          :class="{ 'orange': !isProposalExpired && !this.proposal.is_accepted, 'grey': isProposalExpired || isDeleted || this.proposal.is_accepted }" 
          class="button ask-question" 
          @click="openAskQuestionModal"> 
          Ask Question
        </button>
      </div>

      <div class="sidebar-section">
        <h3>Vertex Lead</h3>
        <p>{{ this.vertex_bdm || this.proposal.proposal_owner || "Vertex Cyber Security" }}</p>
      </div>
      <div class="sidebar-section">
        <h3>Contact Number</h3>
        <p>{{ this.vertex_bdm_mobile || this.proposal.proposal_owner_contact || "1300 229 237" }}</p>
      </div>
      <div class="sidebar-section">
        <h3>Contact Email</h3>
        <p>{{ this.vertex_bdm_email || this.proposal.proposal_owner_email || "martin@vtxsecurity.com" }}</p>
      </div>
      
      <div class="sidebar-section">
        <h3 v-if="currentProposalStatus === 'accepted' || currentProposalStatus === 'partially_accepted'">Accepted Date</h3>
        <h3 v-else>Proposal Expiry Date</h3>
        
        <p v-if="!this.proposal.is_accepted && (DaysToAcceptCounter(this.proposal.expiry_date) < -1000)">Not Set</p>
        <p v-else-if="this.proposal.is_accepted">{{ formatUtcToTimeZone(this.proposal.accepted_at) }}</p>
        <p v-else>{{ formatUtcToTimeZone(this.proposal.expiry_date) }}</p>

        <div v-if="currentProposalStatus === 'partially_accepted'" class="status-indicator accepted">
          Partially Accepted
        </div>
        <div v-else-if="currentProposalStatus === 'accepted'" class="status-indicator accepted">
          Accepted
        </div>
        <div v-else-if="currentProposalStatus === 'expired'" class="status-indicator expired">
          Proposal Expired
        </div>
        <div v-else class="status-indicator pending">
          <span v-if="(DaysToAcceptCounter(this.proposal.expiry_date) < -1000)">Countdown TBD</span>
          <span v-else>
            {{ DaysToAcceptCounter(this.proposal.expiry_date) }} {{ DaysToAcceptCounter(this.proposal.expiry_date) == 1 ? 'day' : 'days' }} left
          </span>
        </div>
      </div>
      <div class="bottom-actions">
        <div class="sample-contract">
          <button class="button view-sample-contract" @click="openSampleContractsModal">View Sample Contract Terms</button>
        </div>

      </div>
    </aside>

    <ShareProposalModal
      :show="showShareModal"
      :existing-shared-users="proposal.shared_users"
      :proposal-access-key="this.access_key"
      :proposal-unique-key="this.unique_key"
      :lead-email="proposal.Lead.email"
      @close="closeShareModal"
      @share-successful="handleShareSuccess"
      @revoke-successful="handleShareRevoked"
      @share-failed="handleShareFailure"
    />

    <AskQuestionModal
      :show="showAskQuestionModal"
      :proposal-unique-key="this.unique_key"
      :proposal-access-key="this.access_key"
      :client-email="proposal.Lead.email"
      @close="closeAskQuestionModal"
      @question-submitted="this.$toast.success('Your question has been sent successfully!')"
      @question-failed="this.$toast.error('Failed to submit question. Please try again.')"
    />

    <ProceedToContractWizard
      :show="showProceedWizard"
      :show-discount-calculation="proposal.show_discount_calculation"
      :proposal-items="filteredProposalItems"
      :lead="proposal.Lead || proposal.lead"
      :shared-users="proposal.shared_users"
      :proposal-unique-key="this.unique_key"        
      :proposal-access-key="this.access_key"
      :is-proposal-accepted="proposal.is_accepted"
      :accepted-option-index="proposal.option_index_selected"
      :contract-acceptances="proposal.contract_acceptances"
      @close="closeProceedWizard"
      @acceptance-success="getProposal"
    />

    <div v-if="showMeetModal" class="modal-overlay" @click.self="closeMeetModal">
      <div class="modal-content">
        <button class="modal-close-btn" @click="closeMeetModal">&times;</button>
        <h3>Book a Call</h3>
        <iframe
          :src="vertex_bdm_calendar_link"
          style="width: 100%; height: 600px; border: none;"
        ></iframe>
      </div>
    </div>

    <div v-if="showSampleContractsModal" class="modal-overlay" @click.self="closeSampleContractsModal">
      <div class="sample-contract-modal">
        <div class="modal-header-custom">
          <h5 class="modal-title-custom">Sample Contract Terms</h5>
          <button @click="closeSampleContractsModal" class="close-button-custom">&times;</button>
        </div>
        <div class="modal-controls">
          <input v-model.trim="sampleContractSearch" type="text" class="form-control form-control-sm"
            placeholder="🔍 Search..."
            style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif; font-weight: 400;"
            @keyup="filterSampleContracts">
        </div>
        <div class="modal-body-custom">
          <div v-if="isModalLoading" class="no-results">Loading...</div>
          <ul v-else-if="filteredSampleContracts.length > 0" class="contract-list">
            <li v-for="contract in filteredSampleContracts" :key="contract.id" @click="viewContract(contract)" class="contract-item-card">
              <div class="contract-info">
                <p class="contract-name">{{ contract.name }}</p>
                <p class="contract-details"><strong>Type:</strong> {{ contract.contractType }}</p>
                <p class="contract-details"><strong>Version:</strong> {{ contract.version }}</p>
              </div>
            </li>
          </ul>
          <div v-else class="no-results">
            <p>No sample contracts found.</p>
          </div>
        </div>
      </div>
    </div>

  </div>

  <div v-if="toast.show" class="toast" :class="`toast-${toast.type}`">
    {{ toast.message }}
  </div>
</template>

<script>
import Carousel from '@/components/Proposals/Carousel.vue';
import { isEmpty, toOptionName } from '../../../plugins/helper';
import { getBusinessNumberShortLabel } from '@/plugins/businessValidator.js';
import LoadingPage from '@/components/Utilities/vertex-loading.vue';
import ShareProposalModal from './ShareProposal.vue';
import AskQuestionModal from './AskQuestionModal.vue';
import ProceedToContractWizard from './ProceedToContractWizard.vue'; 
import VLogo from '@/assets/img/V.svg';
import VertexWhiteLogo from '@/assets/img/Vertex_white.svg';

export default {
  name: 'ProposalPage',

  components: {
    Carousel,
    LoadingPage,
    ShareProposalModal,
    AskQuestionModal,
    ProceedToContractWizard
  },

  props: {
    unique_key: { type: String, required: true },
    access_key: { type: String, required: true }
  },

  data() {
    return {
      rawAuthProducts: [], 
      rawInternalPricing: [], 
      allMappedProducts: [], 
      showAskQuestionModal: false,
      showShareModal: false,
      showProceedWizard: false, 
      toast: { show: false, message: '', type: 'success' },
      isExpired: false,
      isDeleted: false,
      showMeetModal: false,
      showSampleContractsModal: false,
      isSidebarCollapsed: false,
      isMobileDrawerOpen: false,
      fullLogo: VertexWhiteLogo,
      collapsedLogo: VLogo,
      testimonials: [],
      clientTestimonials: [],
      proposal: {
        Lead: { organisation_name: "", abn: "", first_name: "", last_name: "", is_charity: false, email: "" },
        // Updated variables mapped from your backend:
        proposal_section_content: { cover_page_content: "", product_service_content: "", image_grid_content: "", testimonials_content: "", content_social_impact: "", content_commitment: "", content_whs_responsibilities: "", content_terms_conditions: "", content_next_steps: "" },
        proposal_title: "",
        proposal_owner: "",
        expiry_date: "",
        proposal_items: [],
        proposal_version: "1.0",
        is_sent: false,
        show_discount_calculation: false,
        proposal_reissue: true,
        is_accepted: false,
        option_index_selected: null,
        contract_acceptances: [],
      },
      ids: [],
      proposal_items: [],
      filteredProposalItems: [],
      defaultSectionOrder: [
        'CoverPage', 'ProductService', 'PricingTable', 'ImageGrid',
        'Testimonials', 'SocialImpact', 'Commitment',
        'TermsConditions', 'NextSteps'
      ],
      isLoading: false,
      isModalLoading: false,
      keyType: null,
      IsHidden: true,
      sampleContracts: [],
      filteredSampleContracts: [],
      sampleContractSearch: '',
      vertex_bdm: '',
      vertex_bdm_email: '',
      vertex_bdm_mobile: '',
      vertex_bdm_calendar_link: '',
    };
  },

  created() {
    this.init();
  },

  computed: {
    businessNumberShortLabel() {
      const code = this.proposal?.Lead?.country || this.proposal?.Lead?.country_code || this.proposal?.Lead?.organisation?.country || 'AU';
      return getBusinessNumberShortLabel(code);
    },
    currentProposalStatus() {
      if (this.proposal.is_accepted) {
        if (this.canAcceptAdditionalItems) return 'partially_accepted';
        return 'accepted';
      }
      if (this.isProposalExpired) return 'expired';
      return 'pending';
    },

    canAcceptAdditionalItems() {
      if (!this.proposal.is_accepted || !this.proposal.option_index_selected || !this.proposal.proposal_items || this.proposal.proposal_items.length === 0 || this.isProposalExpired) {
        return false;
      }
      const itemsInSelectedOption = this.proposal.proposal_items.filter(item => item.option_index === this.proposal.option_index_selected && !item.item_hidden);
      if (itemsInSelectedOption.length === 0) return false;

      const hasUnaccepted = itemsInSelectedOption.some(item => !item.added_to_contract);
      const hasAccepted = itemsInSelectedOption.some(item => item.added_to_contract);
      return hasUnaccepted && hasAccepted;
    },

    visibleSharedUsers() {
      if (!this.proposal.shared_users) return [];
      return this.proposal.shared_users.filter(user => !user.is_access_revoked);
    },

    acceptedProposalItems() {
        if (!this.proposal.is_accepted) return [];
        
        // FIX: Re-added the !item.item_hidden check here
        const acceptedItems = this.proposal.proposal_items.filter(item => item.added_to_contract && !item.item_hidden);
        
        if (acceptedItems.length > 0) {
            const processedItems = acceptedItems.map(item => {
                const targetId = item.product_id || (item.Product && item.Product.id) || (item.Product && item.Product.uuid);
                const isChild = this.checkIfChild(targetId, item.Product);
                let planType = 'One-Off';
                let pricingPlanType = '';

                let mappedProduct = null;
                if (this.allMappedProducts && this.allMappedProducts.length > 0) {
                    mappedProduct = this.allMappedProducts.find(prod => prod.id === targetId);
                    if (mappedProduct && mappedProduct.contractType) planType = mappedProduct.contractType;
                    if (mappedProduct && mappedProduct.pricingPlanType) pricingPlanType = mappedProduct.pricingPlanType;
                }
                if (this.rawInternalPricing && this.rawInternalPricing.length > 0) {
                    const allMatches = this.rawInternalPricing.filter(ip => ip.product_id === targetId || ip.ProductID === targetId || ip.auth_item_code === targetId || ip.AuthItemCode === targetId);
                    for (const ip of allMatches) {
                        if (!pricingPlanType && ip.PricingPlanType) pricingPlanType = ip.PricingPlanType;
                        if (planType === 'One-Off' && ip.ContractBasis) planType = ip.ContractBasis;
                    }
                }

                const resolvedProductName = (item.product_name && item.product_name.trim()) ? item.product_name : (mappedProduct?.name || item.Product?.display_name || item.Product?.name || "Unknown Product");
                return { ...item, product: resolvedProductName, product_name: resolvedProductName, isChild: isChild, planType: planType, pricingPlanType: pricingPlanType };
            });

            return [{
                option_index: this.proposal.option_index_selected,
                productList: processedItems
            }];
        }
        return [];
    },

    mainOptionProductList() {
      const mainOption = this.filteredProposalItems.find(pi => pi.option_index === 1);
      return mainOption ? mainOption.productList : [];
    },

    isStaticView() {
      return (
        this.keyType === 'static' ||
        (this.proposal && this.proposal.key_type === 'static')
      );
    },

    isProposalExpired() {
      if (this.keyType === 'internal' || this.isStaticView) return false;
      if (this.proposal.proposal_status === 0) return false;
      if (!this.proposal.expiry_date || this.DaysToAcceptCounter(this.proposal.expiry_date) < -1000) return false;
      return this.isExpired || this.DaysToAcceptCounter(this.proposal.expiry_date) <= 0;
    },

    canShareProposal() {
      if (this.isProposalExpired || this.isDeleted || this.isStaticView) return false;
      return this.proposal.key_type !== 'internal' && this.proposal.share_type === 'signer';
    },

    canAcceptProposal() {
      if (this.isStaticView) return false;
      return this.proposal.key_type !== 'internal' && this.proposal.share_type !== 'viewer';
    },

    orderedSections() {
      const sequence = this.proposal.proposal_section_sequence;
      // Fallback for old proposals that don't have sequence data
      if (!sequence || sequence.length === 0) {
        return this.defaultSectionOrder.filter(s => s !== 'WHS');
      }
      return sequence
        .filter(s => !s.is_hidden && s.section_name !== 'WHS')
        .sort((a, b) => a.sequence - b.sequence)
        .map(s => s.section_name);
    }
  },

  methods: {
    toOptionName,

    hasContent(field) {
      const content = this.proposal.proposal_section_content?.[field];
      return content !== undefined && content !== null && content !== '' && !this.isPlaceholderOrEmptyHTML(content);
    },

    isPlaceholderOrEmptyHTML(html) {
      if (!html) return true;
      const temp = document.createElement("div");
      temp.innerHTML = html;
      const text = temp.textContent || temp.innerText || "";
      const trimmed = text.trim();
      if (trimmed === "") {
        const hasMedia = temp.querySelector("img, iframe, svg, video, audio") !== null;
        if (!hasMedia) return true;
      }
      const placeholders = [
        "[Enter your introductory text for the pricing table here...]",
        "[Enter pricing table introduction...]",
        "[Enter any concluding remarks or notes below the pricing table here...]",
        "[Enter pricing table notes...]"
      ];
      return placeholders.includes(trimmed);
    },
    
    async init() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.getAuthProducts(),
          this.getInternalPricing()
        ]);
        this.buildAvailableProductsList();
        await this.getProposal();
      } finally {
        this.isLoading = false;
      }
    },

    async getAuthProducts() {
      try {
        const response = await this.$axios_internal.get("/auth-products");
        this.rawAuthProducts = response.data.authProducts || [];
      } catch (error) {
        this.rawAuthProducts = [];
      }
    },

    async getInternalPricing() {
      try {
        const response = await this.$axios_internal.get("/public/products/pricing");
        this.rawInternalPricing = response.data.productPrices || [];
      } catch (error) {
        this.rawInternalPricing = [];
      }
    },

    buildAvailableProductsList() {
      this.allMappedProducts = [];
      this.rawAuthProducts.forEach(authItem => {
        if (!authItem.Product) return;

        // Parse parent_product_ids JSON array (supports both old string and new array format)
        let parentIds = authItem.parent_product_ids || authItem.ParentProductIDs || authItem.parent_product_id || authItem.ParentProductID || null;
        if (typeof parentIds === 'string') {
           try { parentIds = JSON.parse(parentIds); } catch(e) { parentIds = parentIds ? [parentIds] : []; }
        }
        if (!Array.isArray(parentIds)) parentIds = parentIds ? [parentIds] : [];
        parentIds = parentIds.filter(id => id && id !== '');

        this.allMappedProducts.push({
          id: authItem.product_id || authItem.Product.id || authItem.Product.uuid,
          parentIds: parentIds,
          contractType: authItem.contract_type || (authItem.Product && authItem.Product.contract_type) || authItem.contract_basis || (authItem.Product && authItem.Product.contract_basis) || "",
          pricingPlanType: authItem.pricing_plan_type || ''
        });
      });
    },

    checkIfChild(productId, productPayloadObj = null) {
      if (!productId) return false;
      let targetId = productId;
      
      if (this.rawInternalPricing && this.rawInternalPricing.length > 0) {
        const internalMap = this.rawInternalPricing.find(ip => 
            ip.ProductID === targetId || 
            ip.product_id === targetId || 
            ip.id === targetId ||
            ip.AuthItemCode === targetId ||
            ip.auth_item_code === targetId
        );
        
        if (internalMap) {
            // Check parent_product_ids (JSON array string) from the public pricing endpoint
            let rawParentIds = internalMap.ParentProductIDs || internalMap.parent_product_ids || internalMap.ParentProductID || internalMap.parent_product_id || null;
            if (rawParentIds) {
                let parsedIds = rawParentIds;
                if (typeof parsedIds === 'string') {
                    try { parsedIds = JSON.parse(parsedIds); } catch(e) { parsedIds = parsedIds ? [parsedIds] : []; }
                }
                if (!Array.isArray(parsedIds)) parsedIds = parsedIds ? [parsedIds] : [];
                if (parsedIds.filter(id => id && id !== '').length > 0) return true;
            }

            const isAuthLinked = internalMap.HasAuthProductLinked == 1 || internalMap.has_auth_product_linked == 1 || internalMap.HasAuthProductLinked === true;
            if (isAuthLinked && (internalMap.AuthItemCode || internalMap.auth_item_code)) {
                targetId = internalMap.AuthItemCode || internalMap.auth_item_code;
            }
        }
      }

      if (this.allMappedProducts && this.allMappedProducts.length > 0) {
          const mappedProd = this.allMappedProducts.find(item => item.id === targetId);
          if (mappedProd && mappedProd.parentIds && mappedProd.parentIds.length > 0) {
              return true;
          }
      }

      if (productPayloadObj) {
          // Support both new array and old string format from the product payload
          let pIds = productPayloadObj.ParentProductIDs || productPayloadObj.parent_product_ids || productPayloadObj.ParentProductID || productPayloadObj.parent_product_id || null;
          if (pIds) {
              if (typeof pIds === 'string') {
                  try { pIds = JSON.parse(pIds); } catch(e) { pIds = pIds ? [pIds] : []; }
              }
              if (!Array.isArray(pIds)) pIds = pIds ? [pIds] : [];
              if (pIds.filter(id => id && id !== '').length > 0) return true;
          }
      }

      return false;
    },

    scrollToPricing() {
      const element = document.getElementById('pricing_table_section');
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    toggleSidebar() { this.isSidebarCollapsed = !this.isSidebarCollapsed; },
    toggleMobileDrawer() { this.isMobileDrawerOpen = !this.isMobileDrawerOpen; },
    closeMobileDrawer() { this.isMobileDrawerOpen = false; },
    openAskQuestionModal() { this.showAskQuestionModal = true; },
    closeAskQuestionModal() { this.showAskQuestionModal = false; },
    openMeetModal() { this.showMeetModal = true; },
    closeMeetModal() { this.showMeetModal = false; },
    openProceedWizard() { this.showProceedWizard = true; },
    closeProceedWizard() { this.showProceedWizard = false; },
    openSampleContractsModal() {
      this.showSampleContractsModal = true;
      if (this.sampleContracts.length === 0) this.fetchSampleContracts();
    },
    closeSampleContractsModal() { this.showSampleContractsModal = false; },

    async fetchSampleContracts() {
      this.isModalLoading = true;
      try {
        const response = await this.$axios_internal.get('/samples/sample-contracts');
        this.sampleContracts = response.data.sample_contracts.map(contract => ({
          id: contract.id,
          name: contract.name,
          contractType: contract.contract_type || 'Sample Terms',
          version: contract.version || 'v1.0',
          full_link: contract.full_link,
        }));
        this.filterSampleContracts();
      } catch (error) {
        this.sampleContracts = [];
        this.filteredSampleContracts = [];
      } finally {
        this.isModalLoading = false;
      }
    },

    filterSampleContracts() {
      if (!this.sampleContractSearch) {
        this.filteredSampleContracts = this.sampleContracts;
        return;
      }
      const searchTerm = this.sampleContractSearch.toLowerCase();
      this.filteredSampleContracts = this.sampleContracts.filter(contract =>
        contract.name.toLowerCase().includes(searchTerm)
      );
    },

    viewContract(contract) {
      if (contract.full_link) window.open(contract.full_link, '_blank');
      else this.$toast.info("This contract does not have a file to view.");
    },

    formatUtcToTimeZone(utcDateString) {
      const date = new Date(utcDateString);
      if(date.toString() === "Invalid Date") return "";
      return new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'long', year: 'numeric', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }).format(date);
    },

    DaysToAcceptCounter(utcDateString) {
      const expiryDate = new Date(utcDateString);
      const now = new Date();
      const differenceInMs = new Date(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate()).getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      return Math.round(differenceInMs / (1000 * 60 * 60 * 24));
    },

    calculateOptionTotalPrice(productList) {
      return productList.reduce((total, product) => {
        if (product.is_heading || product.IsHeading) return total;
        if (product.isChild && parseFloat(product.price) <= 0) return total; 
        const productTotal = ((parseFloat(product.price) || 0) - (parseFloat(product.discount) || 0)) * (parseInt(product.quantity) || 0);
        return total + productTotal;
      }, 0) * 1.1;
    },

    calculateOptionSubTotalPrice(productList) {
      return productList.reduce((total, product) => {
        if (product.is_heading || product.IsHeading) return total;
        if (product.isChild && parseFloat(product.price) <= 0) return total; 
        const productTotal = ((parseFloat(product.price) || 0) - (parseFloat(product.discount) || 0)) * (parseInt(product.quantity) || 0);
        return total + productTotal;
      }, 0);
    },

    calculateOptionGST(productList) {
      return this.calculateOptionSubTotalPrice(productList) * 0.1;
    },

    formatCurrency(value) {
      const numericValue = parseFloat(value);
      if (isNaN(numericValue)) return '$0.00';
      return `$${numericValue.toFixed(2)}`;
    },

    calculateTotal(item) {
      if (item.is_heading || item.IsHeading) return 0;
      if (item.isChild && parseFloat(item.price) <= 0) return 0; 
      return ((item.price || 0) - (item.discount || 0)) * (item.quantity || 0);
    },

    isUUID(uuid) {
      if (typeof uuid !== 'string') return false;
      if (uuid.length !== 36) return false;
      if (uuid[8] !== '-' || uuid[13] !== '-' || uuid[18] !== '-' || uuid[23] !== '-') return false;
      return true;
    },

    filterProposalItems(proposal_items) {
      if (!isEmpty(proposal_items)) {
        this.ids = [];
        this.filteredProposalItems = [];
        const groupedItems = {};
        
        proposal_items.forEach((p) => {
          // FIX: Re-added this line so hidden items are completely skipped!
          if (p.item_hidden) return; 

          if (!groupedItems[p.option_index]) {
            groupedItems[p.option_index] = { productList: [], option_index: p.option_index };
          }
          
          if (p.is_heading || p.IsHeading) {
            groupedItems[p.option_index].productList.push({
              ...p,
              is_heading: true,
              isChild: false,
              planType: 'One-Off',
              pricingPlanType: ''
            });
            return;
          }

          const targetId = p.product_id || (p.Product && p.Product.id) || (p.Product && p.Product.uuid);
          const isChild = this.checkIfChild(targetId, p.Product);
          
          let planType = 'One-Off';
          let pricingPlanType = '';

          let mappedProduct = null;
          if (this.allMappedProducts && this.allMappedProducts.length > 0) {
              mappedProduct = this.allMappedProducts.find(prod => prod.id === targetId);

              if (mappedProduct && mappedProduct.contractType) {
                  planType = mappedProduct.contractType;
              }
              if (mappedProduct && mappedProduct.pricingPlanType) {
                  pricingPlanType = mappedProduct.pricingPlanType;
              }
          }
          if (this.rawInternalPricing && this.rawInternalPricing.length > 0) {
              const allMatches = this.rawInternalPricing.filter(ip => 
                  ip.product_id === targetId || ip.ProductID === targetId ||
                  ip.auth_item_code === targetId || ip.AuthItemCode === targetId
              );
              for (const ip of allMatches) {
                  if (!pricingPlanType && ip.PricingPlanType) pricingPlanType = ip.PricingPlanType;
                  if (planType === 'One-Off' && ip.ContractBasis) planType = ip.ContractBasis;
              }
          }

          
          const resolvedProductName = (p.product_name && p.product_name.trim()) ? p.product_name : (mappedProduct?.name || p.Product?.display_name || p.Product?.name || "Unknown Product");
          const processedItem = { ...p, product: resolvedProductName, product_name: resolvedProductName, isChild: isChild, planType: planType, pricingPlanType: pricingPlanType };

          if(p.option_index == 1 && targetId) {
             let hasInternalMap = false;
             if (this.rawInternalPricing && this.rawInternalPricing.length > 0) {
                 const linkedInternalProducts = this.rawInternalPricing.filter(ip => {
                     const isAuthLinked = ip.has_auth_product_linked == 1 || ip.HasAuthProductLinked == 1 || ip.has_auth_product_linked === true || ip.HasAuthProductLinked === true;
                     const authCode = ip.auth_item_code || ip.AuthItemCode;
                     const pId = ip.product_id || ip.ProductID || ip.id;
                     return (isAuthLinked && authCode === targetId) || (pId === targetId);
                 });
                 
                 if (linkedInternalProducts.length > 0) {
                     hasInternalMap = true;
                     linkedInternalProducts.forEach(ip => {
                         const mappedId = ip.product_id || ip.ProductID || ip.id;
                         if (mappedId) this.ids.push(mappedId.toString());
                     });
                 }
             }
             if (!hasInternalMap) this.ids.push(targetId);
          }
          groupedItems[p.option_index].productList.push(processedItem);
        });
        
        this.ids = [...new Set(this.ids)];
        this.filteredProposalItems = Object.values(groupedItems);
        this.filteredProposalItems.sort((a, b) => a.option_index - b.option_index);
      } else {
        this.filteredProposalItems = [];
      }
    },

    hasNotes(productList) {
      return productList.some(item => item.remarks && item.remarks.trim() !== '');
    },

    mapTestimonialsToProposal(testimonials) {
      this.clientTestimonials = [];
      testimonials.forEach(testimonial => {
        let initials = this.getFirstLettersOfName(testimonial.contact_name);
        this.clientTestimonials.push({
          quote: testimonial.testimonial,
          name: testimonial.contact_name,
          avatar: (testimonial.testimonial_image && testimonial.testimonial_image.full_link) ? testimonial.testimonial_image.full_link : `https://placehold.co/600x400?text=${initials}`,
          company: testimonial.organisation_name,
          role: testimonial.contact_role,
        });
      });
    },

    async getTestimonials(productIds) {
      if (!productIds || productIds.length === 0) {
        const response = await this.$axios_internal.get("/proposal/get-testimonials");
        this.mapTestimonialsToProposal(response.data.testimonials || []);
        return;
      }
      try {
        const response = await this.$axios_internal.get("/proposal/get-testimonials");
        const allTestimonials = response.data.testimonials || [];
        let matchedTestimonials = [];
        let hasMissingTestimonial = false;

        productIds.forEach(productId => {
          const found = allTestimonials.filter(t => {
            const relations = t.products || [];
            return relations.some(tp => 
              tp.product_id === productId || 
              (tp.Product && tp.Product.id === productId) ||
              (tp.Product && tp.Product.uuid === productId)
            );
          });
          
          if (found.length > 0) matchedTestimonials.push(...found);
          else hasMissingTestimonial = true; 
        });

        if (hasMissingTestimonial) this.mapTestimonialsToProposal(allTestimonials);
        else this.mapTestimonialsToProposal([...new Set(matchedTestimonials)]);
      } catch (error) {
        // Failed to fetch testimonials
      }
    },

    async getProposal() {
      try {
        const response = await this.$axios.get(`/proposal/view-proposal/${this.unique_key}/${this.access_key}`);

        const proposalData = response.data.vertex_proposal;
        this.keyType = proposalData.key_type;
        const hasExpiry = proposalData.expiry_date && this.DaysToAcceptCounter(proposalData.expiry_date) >= -1000;
        this.isExpired = proposalData.proposal_status !== 0 && hasExpiry && this.DaysToAcceptCounter(proposalData.expiry_date) <= 0;

        if(response.data.vertex_proposal && response.data.vertex_proposal.shared_users && response.data.vertex_proposal.shared_users.length > 0) {
            response.data.vertex_proposal.shared_users.forEach(shared_user => {
                if(shared_user.shared_external_key == this.access_key && shared_user.is_access_revoked) {
                  this.$router.push({ name: 'ProposalNoAccess' });
                  return;
                }
            })
        }

        if (!this.keyType || !response.data.vertex_proposal || response.data.vertex_proposal.deleted_at) {
         this.$router.push({ name: 'ProposalDeletedNonExist' });
         return;
        }

        this.proposal = response.data.vertex_proposal;
        document.title = this.proposal.proposal_title || "Vertex Cyber Security Proposal";

        if (!this.isStaticView && this.isProposalExpired && this.proposal.is_accepted == false) {
            this.$router.push({ name: 'ProposalExpired', params: { unique_key: this.unique_key, access_key: this.access_key } });
            return;
        }

        this.filterProposalItems(this.proposal.proposal_items);
        this.getTestimonials(this.ids);

        if (this.proposal.bdm) {
          this.vertex_bdm = `${this.proposal.bdm.first_name} ${this.proposal.bdm.last_name}`;
          this.vertex_bdm_email = this.proposal.bdm.email;
          this.vertex_bdm_mobile = this.proposal.bdm.mobile;
          this.vertex_bdm_calendar_link = this.proposal.bdm.calendar_link
        } else if (this.proposal.proposal_owner && this.proposal.proposal_owner_contact && this.proposal.proposal_owner_email) {
          this.vertex_bdm = this.proposal.proposal_owner;
          this.vertex_bdm_email = this.proposal.proposal_owner_email;
          this.vertex_bdm_mobile = this.proposal.proposal_owner_contact;
          this.vertex_bdm_calendar_link = process.env.VUE_APP_GOOGLE_CALENDAR_LINK;
        } else {
          this.vertex_bdm = "Vertex Cyber Security";
          this.vertex_bdm_email = "Martin@vtxsecurity.com";
          this.vertex_bdm_mobile = "1300 229 237";
          this.vertex_bdm_calendar_link = process.env.VUE_APP_GOOGLE_CALENDAR_LINK;
        }

        if (this.keyType === 'internal') this.IsHidden = false;

        if (this.$route.query.accept == '1' && !this.proposal.is_accepted && !this.isProposalExpired && this.canAcceptProposal) {
            this.openProceedWizard();
            this.$router.replace({ query: null }); 
        }
      } catch (error) {
        if (error.response && error.response.status === 403 && error.response.data && error.response.data.message === 'access_revoked') {
            this.$router.push({ name: 'ProposalNoAccess' });
        } else {
            this.$router.push({ name: 'ProposalDeletedNonExist' });
        }
      }
    },

    getFirstLettersOfName(contact_name) {
      return contact_name.split(' ').filter(name => name.length > 0).map(name => name[0].toUpperCase()).join('');
    },

    async handleDeclineReissue() {
      this.isLoading = true;
      this.$toast.info("Updating proposal status...");
      try {
         await this.$axios_internal.post(`/proposals/decline-reissue`, { unique_key: this.unique_key, access_key: this.access_key });
        this.proposal.proposal_reissue = false;
        this.$toast.success("Your preference has been saved.");
        this.$router.push({ name: 'ProposalFeedback', params: { unique_key: this.unique_key, access_key: this.access_key } })
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Could not update the proposal. Please try again.");
      } finally {
        this.isLoading = false;
      }
    },

    async downloadProposalPdf() {
      this.isLoading = true;
      this.$toast.info("Generating PDF on the server, please wait...");
      
      try {
          const response = await this.$axios.get(`/proposals/${this.unique_key}/${this.access_key}/pdf/external`, {
              responseType: 'blob', 
              headers: { 'Accept': 'application/pdf' }
          });

          if (!response.data || response.data.size === 0) {
              this.$toast.error("Error: The server returned an empty PDF.");
              return;
          }

          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          
          link.style.display = 'none';
          link.href = url;

          let fileName = this.proposal.proposal_title ? `${this.proposal.proposal_title.replace(/[^a-z0-9]/gi, '_')}.pdf` : 'Vertex_Proposal.pdf';
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();

          setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
          }, 200);

          this.$toast.success("PDF downloaded successfully!");
      } catch (error) {
          this.$toast.error("Failed to generate the PDF. Please try again.");
      } finally {
          this.isLoading = false;
      }
    },

    openShareModal() { this.showShareModal = true; },
    closeShareModal() { this.showShareModal = false; },
    handleShareSuccess(sharedUsers) {
      this.proposal.shared_users = sharedUsers;
      this.$toast.success('Proposal shared successfully!');
    },
    handleShareRevoked(sharedUsers) {
      this.proposal.shared_users = sharedUsers;
      this.$toast.success('User access has been successfully revoked.');
    },
    handleShareFailure(errorMessage) { this.$toast.error(errorMessage); },
    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
      setTimeout(() => { this.toast.show = false; }, 3000);
    }, 
    handleAcceptReissue() {
      if (this.proposal.renewal_token) {
        window.location.href = `/renew-proposal/${this.unique_key}/${this.access_key}?token=${this.proposal.renewal_token}`;
      } else {
        this.$toast.error("Renewal token is missing. Please contact support.");
      }
    },
  },
};
</script>

<style scoped>
/* (All of your original styles remain unchanged below here) */
.proposal-container {
  position: relative;
  display: flex;
  height: 100vh; 
  overflow: hidden; 
  font-family: 'Roboto', sans-serif !important;
  color: #333;
  background-color: #f0f8ff;
  width: 100%;
}
.sidebar {
  transition: width 0.3s ease, flex-basis 0.3s ease;
  background-color: #435666;
  padding: 20px;
  flex-basis: 250px;
  display: flex;
  flex-direction: column;
  overflow: visible;
  flex-shrink: 0;
  position: relative;
}
.sidebar-logo { display: flex; flex-direction: column; align-items: flex-start; margin-bottom: 20px; }
.sidebar-logo .logo { max-height: 60px; margin-bottom: 10px; }
.sidebar-logo hr { width: 100%; border: 1px solid #b0bec5; margin: 10px 0; }
.sidebar-section { margin-bottom: 20px; }
.sidebar-section h3 { font-size: 1em; margin-bottom: 8px; color: white; font-weight: bold; }
.sidebar-section p { font-size: 0.9em; color: whitesmoke; border: 1px solid #b0bec5; padding: 10px 20px; border-radius: 10px; display: inline-block; width: 100%; box-sizing: border-box; }
.status-indicator { display: inline-block; padding: 10px 20px; border-radius: 6px; font-size: 0.9em; color: white; width: 100%; box-sizing: border-box; text-align: center; font-weight: bold; margin-top: 5px; }
.status-indicator.active { background-color: #4caf50; }
.status-indicator.not-active { background-color: grey; }
.status-indicator.accepted { background-color: green; }
.status-indicator.partially-accepted { background-color: #00a0c1; }
.status-indicator.expired { background-color: #f3994d; }
.status-indicator.pending { background-color: #5a6e80; }
.main-content { flex-grow: 1; padding: 25px; max-width: none; max-height: 100vh; overflow-y: auto; transition: margin-left 0.3s ease; }
.main-content.expanded { margin-left: 10px; }
.proposal-header { display: flex; align-items: center; margin-bottom: 25px; background-color: #fff; padding: 15px 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(150, 131, 131, 0.05); }
.header-text h1 { font-size: 1.5em; color: darkslategray; margin: 0; }
.header-actions { margin-left: auto; }
.header-actions button, .header-actions span { margin-left: 10px; padding: 8px 16px; border: none; cursor: pointer; font-weight: bold; font-size: 0.9em; }
.header-actions .primary { background-color: #1976d2; color: white; }
.additional-actions .primary { background-color: #1976d2; color: white; }
.header-actions .accepted-proposal { background-color: green; color: white; }
.header-actions .accepted-proposal.partially-accepted { background-color: #e67e22; }
.header-actions .secondary { background-color: #6c757d; color: white; }
.proposal-details { background-color: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); }
.additional-actions { display: flex; justify-content: flex-end; margin-top: 20px; }
.testimonial-section { background-color: #fff; padding: 20px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); }
.proposal-introduction, .product-services, .content-blocks { margin-bottom: 15px; line-height: 1.6; }
.proposal-introduction p, .product-services p, .content-blocks p { margin-bottom: 10px; }
.proposal-introduction p:last-child, .product-services p:last-child, .content-blocks p:last-child { margin-bottom: 0; }
.right-sidebar { background-color: #435666; padding: 25px; flex-basis: 300px; display: flex; flex-direction: column; position: relative; min-height: 100vh; max-height: 100vh; flex-shrink: 0; }
.top-actions { display: flex; flex-direction: column; gap: 12px; margin-bottom: 25px; }
.bottom-actions { display: flex; flex-direction: column; gap: 15px; padding-top: 25px; position: absolute; bottom: 25px; left: 0; right: 0; background-color: inherit; padding: 25px; }
.right-sidebar .sidebar-section h3 { font-size: 1em; margin-bottom: 10px; color: white; font-weight: bold; }
.right-sidebar .sidebar-section p { font-size: 0.9em; color: whitesmoke; }
.expiry-countdown { color: #f3994d !important; border: none !important; padding-left: 5px !important; font-weight: bold; }
.proposal-accepted-sidebar-label { color: rgba(25, 135, 84, 1.0) !important; border: none !important; padding-left: 5px !important; font-weight: bold; }
.top-actions .proceed-to-contract { background-color: #1976d2; color: white; }
.top-actions .share { background-color: #00a0c1; color: white; }
.top-actions .book-call { background-color: rgba(25, 135, 84, 1.0); color: white; }
.top-actions .ask-question { background-color: #ffa000; color: white; }
.bottom-actions .log-in-button { width: 100%; padding: 10px 18px; border: none; border-radius: 6px; background-color: #00a0c1; color: white; font-weight: bold; cursor: pointer; font-size: 0.95em; margin-top: 0; }
.bottom-actions .view-sample-contract { width: 100%; padding: 10px 18px; border: none; border-radius: 6px; background-color: grey; color: white; font-weight: bold; cursor: pointer; font-size: 0.95em; margin-top: 0; }
.button { padding: 10px 18px; border: none; border-radius: 6px !important; cursor: pointer; font-weight: bold; font-size: 0.95em; }
::-webkit-scrollbar { width: 10px; }
::-webkit-scrollbar-track { background: #f1f1f1; }
::-webkit-scrollbar-thumb { background: #00a0c1; }
::-webkit-scrollbar-thumb:hover { background: #00a0c1; }
.product-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; border: 1px solid #ddd; }
.product-table th, .product-table td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; vertical-align: top; }
.product-table th { background-color: #00bcd4; color: white; font-weight: bold; vertical-align: middle; }
.product-table tr:last-child td { border-bottom: none; }
.total-row { font-weight: bold; background-color: #f9f9f9; }
.total-label { text-align: right; padding-right: 8px; }
.total-amount { text-align: right; }
.tab-wrapper { margin: 30px auto; max-width: 100%; padding: 0 15px; }
.nav-tabs { border-bottom: 1px solid #dee2e6; margin-bottom: 0; }
.nav-tabs .nav-link { border-radius: 0 !important; border: 1px ridge grey; border-bottom-color: #dee2e6; margin-bottom: -1px; background-color: #e9ecef; color: #495057; font-weight: 500; padding: 10px 15px; cursor: pointer; }
.nav-tabs .nav-link.active { color: #495057; background-color: #fff; border-color: #dee2e6 #dee2e6 #fff; font-weight: bold; }
.tab-content { border: 1px solid #dee2e6; border-top: none; padding: 20px; background-color: #fff; }
.content-blocks.image-grid-section { max-width: 100%; }
.content-blocks.image-grid-section :deep(table) { display: table !important; width: 100%; table-layout: fixed; border-collapse: collapse; }
.content-blocks.image-grid-section :deep(td) { padding: 8px; vertical-align: middle; text-align: center; }
.content-blocks.image-grid-section :deep(img) { max-width: 100% !important; height: auto !important; object-fit: contain; border-radius: 8px; box-shadow: 0 2px 5px rgba(0,0,0,0.1); }
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.7); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background-color: white; padding: 25px; border-radius: 8px; width: 90%; max-width: 900px; max-height: 90vh; position: relative; display: flex; flex-direction: column; }
.modal-content h3 { margin-bottom: 20px; font-size: 1.5em; color: #333; }
.modal-close-btn { position: absolute; top: 10px; right: 15px; background: none; border: none; font-size: 2em; color: #888; cursor: pointer; }
.sample-contract-modal { background-color: white; border-radius: 8px; width: 90%; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 5px 15px rgba(0,0,0,0.3); max-width: 550px; }
.proceed-modal-custom { max-width: 650px; }
.modal-header-custom { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid #e9ecef; flex-shrink: 0; }
.modal-title-custom { font-size: 1.25rem; font-weight: 500; margin: 0; color: #333; }
.close-button-custom { background: none; border: none; font-size: 1.75rem; font-weight: bold; line-height: 1; color: #6c757d; cursor: pointer; opacity: 0.7; }
.close-button-custom:hover { opacity: 1; }
.modal-controls { padding: 1rem 1.5rem; border-bottom: 1px solid #e9ecef; }
.modal-body-custom { padding: 1.5rem; overflow-y: auto; flex-grow: 1; background-color: #fbfbfb; }
.modal-footer-custom { display: flex; justify-content: flex-end; gap: 10px; padding: 1rem 1.5rem; border-top: 1px solid #e9ecef; flex-shrink: 0; background-color: #f8f9fa; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; }
.contract-list { list-style: none; padding: 0; margin: 0; }
.contract-item-card { background-color: #fff; border: 1px solid #dee2e6; border-radius: 8px; padding: 1rem 1.5rem; margin-bottom: 1rem; cursor: pointer; transition: box-shadow 0.2s ease, transform 0.2s ease; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
.contract-item-card:hover { transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0,0,0,0.08); }
.contract-name { font-weight: 600; }
.contract-details { font-size: 0.85rem; color: #6c757d; }
.items-selection-wrapper { margin-top: 0; }
.items-selection-wrapper p { margin-bottom: 10px; font-weight: 500; }
.proceed-modal-custom .form-group { margin-bottom: 20px; }
.proceed-modal-custom label { display: block; margin-bottom: 8px; font-weight: 500; color: #333; }
.proceed-modal-custom .form-control { width: 100%; padding: 10px; border-radius: 6px; border: 1px solid #ccc; font-size: 1rem; }
.item-list { max-height: 250px; overflow-y: auto; border: 1px solid #eee; border-radius: 6px; padding: 10px; background-color: #fff; }
.item-row { display: flex; align-items: flex-start; padding: 12px 5px; }
.item-row:not(:last-child) { border-bottom: 1px solid #f0f0f0; }
.item-selection { padding-right: 12px; padding-top: 2px; }
.item-details { display: flex; justify-content: space-between; flex-grow: 1; gap: 15px; }
.item-name-desc { display: flex; flex-direction: column; }
.item-description { font-size: 0.85em; color: #6c757d; margin-top: 4px; }
.item-price { font-weight: 500; white-space: nowrap; }
.item-row input[type="checkbox"] { width: 18px; height: 18px; cursor: pointer; accent-color: #00a0c1; }
.item-row label { margin-bottom: 0; cursor: pointer; font-weight: normal; }
.select-all-row { background-color: #f8f9fa; border-bottom: 1px solid #dee2e6; margin-bottom: 5px; align-items: center; }
.contract-summary { margin-top: 20px; padding-top: 15px; border-top: 1px solid #eee; }
.contract-summary h4 { margin-bottom: 10px; font-size: 1.1em; color: #333; }
.summary-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 0.95em; }
.summary-row.total { font-size: 1.1em; border-top: 1px solid #ddd; margin-top: 5px; padding-top: 10px; }
.no-results { padding: 2rem; text-align: center; color: #6c757d; }
.expired-content-wrapper, .deleted-content-wrapper { display: flex; flex-direction: column; align-items: center; justify-content: center; height: calc(100vh - 200px); text-align: center; }
.toast { position: fixed; top: 20px; right: 20px; padding: 15px 20px; border-radius: 8px; color: white; z-index: 2000; }
.grey { background-color: #ced4da !important; color: #6c757d !important; cursor: not-allowed !important; }
.sidebar.collapsed { flex-basis: 60px; }
.collapse-btn { position: absolute; top: 45%; right: 0; transform: translate(50%, -50%); background-color: white; color: #00a0c1; border: 1px solid #00a0c1; border-radius: 50%; width: 35px; height: 35px; z-index: 1000; }
.mobile-top-actions { display: none; }
.shared-user-list { border: 1px solid #b0bec5; padding: 10px 15px; border-radius: 10px; color: whitesmoke; font-size: 0.9em; display: flex; flex-direction: column; gap: 8px; }
.shared-user-item { display: flex; justify-content: space-between; align-items: center; gap: 10px; }
.user-email { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.user-role { text-transform: capitalize; background-color: #5a6e80; padding: 3px 8px; border-radius: 4px; font-size: 0.85em; flex-shrink: 0; }
.jump-to-pricing-btn { margin-top: 8px; background: none; border: 1px solid #00a0c1; color: #00a0c1; padding: 5px 12px; border-radius: 4px; font-size: 0.85rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 6px; }
.jump-to-pricing-btn:hover { background-color: #00a0c1; color: white; }
.header-text { display: flex; flex-direction: column; align-items: flex-start; }
.product-table tr.child-row td { background-color: #f8fbfc !important; color: #555 !important; border-top: 1px dashed #eaeaea !important; }
.parent-product-title { color: #333; font-size: 1.05em; }
.child-product-title { color: #555; font-size: 0.95em; font-weight: 500; }
.badge-included { font-size: 0.85em; background-color: #00a0c1; color: #ffffff; padding: 4px 8px; border-radius: 4px; display: inline-block; font-weight: 600; letter-spacing: 0.5px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); }
.desc-content { font-size: 0.9em; line-height: 1.5; color: #444; text-align: left; }
.desc-content ul, .remarks-content ul { margin-top: 0; margin-bottom: 8px; padding-left: 20px; list-style-type: disc !important; }
.desc-content li, .remarks-content li { margin-bottom: 4px; }
.remarks-content { margin-top: 8px; font-size: 0.85em; color: #666; padding-top: 6px; border-top: 1px dashed #ddd; text-align: left; }
.remarks-badge { font-weight: bold; color: #00a0c1; background: #e0f7fa; padding: 2px 6px; border-radius: 4px; margin-right: 4px; font-size: 0.9em; }

/* ========================================================================
   MOBILE RESPONSIVE — Layout, Pricing Tables & Images
   ======================================================================== */

/* --- Global: Force all v-html images to respect container width --- */
.proposal-introduction :deep(img),
.product-services :deep(img),
.content-blocks :deep(img),
.pricing-intro-content :deep(img),
.pricing-notes-content :deep(img) {
  max-width: 100% !important;
  height: auto !important;
  object-fit: contain;
}

/* --- Global: Prevent v-html tables (from WYSIWYG) from overflowing --- */
.proposal-introduction :deep(table),
.product-services :deep(table),
.content-blocks:not(.image-grid-section) :deep(table),
.pricing-intro-content :deep(table),
.pricing-notes-content :deep(table) {
  max-width: 100%;
  overflow-x: auto;
  display: block;
}

/* --- Hamburger menu button: hidden on desktop, shown on mobile/tablet --- */
.mobile-menu-btn {
  display: none;
  background: none;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 1.1em;
  color: #435666;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.2s ease;
}
.mobile-menu-btn:hover {
  background-color: #f0f0f0;
}

/* --- Mobile drawer backdrop --- */
.mobile-drawer-backdrop {
  display: none;
}

/* --- Tablet (≤ 1024px) --- */
@media (max-width: 1024px) {
  /* Show hamburger button */
  .mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Sidebar becomes off-screen drawer */
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 280px;
    flex-basis: 280px;
    z-index: 1100;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    overflow-y: auto;
  }
  .sidebar.mobile-drawer-open {
    transform: translateX(0);
  }

  .collapse-btn {
    display: none;
  }

  /* Backdrop overlay */
  .mobile-drawer-backdrop {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1050;
  }

  .right-sidebar {
    flex-basis: 240px;
  }

  .product-table th,
  .product-table td {
    padding: 8px 6px;
    font-size: 0.85em;
  }

  .content-blocks.image-grid-section :deep(img) {
    max-width: 48% !important;
  }
}

/* --- Mobile (≤ 768px) --- */
@media (max-width: 768px) {
  /* Stack the entire layout vertically */
  .proposal-container {
    flex-direction: column;
    height: auto;
    overflow: visible;
  }

  /* Sidebar drawer is already handled by ≤1024px rules above */
  .collapse-btn {
    display: none;
  }

  /* Main content fills full width and scrolls naturally */
  .main-content {
    padding: 15px;
    max-height: none;
    overflow-y: visible;
  }

  /* Right sidebar stacks below main content */
  .right-sidebar {
    flex-basis: auto;
    width: 100%;
    min-height: auto;
    max-height: none;
    padding: 20px 15px;
  }

  /* Action buttons go horizontal */
  .top-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .top-actions .button {
    flex: 1;
    min-width: 90px;
    text-align: center;
    font-size: 0.85em;
    padding: 10px 8px;
  }

  /* Remove absolute positioning from bottom actions */
  .bottom-actions {
    position: relative;
    bottom: auto;
    left: auto;
    right: auto;
    flex-direction: row;
    gap: 10px;
  }
  .bottom-actions .button {
    flex: 1;
  }

  /* Stack header title and actions */
  .proposal-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .header-actions {
    margin-left: 0;
    display: flex;
    gap: 8px;
    width: 100%;
  }
  .header-actions button,
  .header-actions span {
    margin-left: 0;
    flex: 1;
    text-align: center;
  }
  .header-text h1 {
    font-size: 1.2em;
  }

  .mobile-top-actions {
    display: flex;
    gap: 8px;
    width: 100%;
    margin-top: 8px;
  }
  .mobile-top-actions .button {
    flex: 1;
    text-align: center;
    font-size: 0.85em;
    padding: 10px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .mobile-top-actions .button.teal { background-color: #00a0c1; color: white; }
  .mobile-top-actions .button.green { background-color: rgba(25, 135, 84, 1.0); color: white; }
  .mobile-top-actions .button.orange { background-color: #ffa000; color: white; }
  .mobile-top-actions .button.grey { background-color: #ced4da; color: #6c757d; }

  /* Hide sidebar top actions on mobile since they are now at the top */
  .top-actions {
    display: none;
  }

  .sample-contract {
    width: 100%;
    display: flex;
    justify-content: center;
  }
  .bottom-actions .view-sample-contract {
    width: auto;
    min-width: 240px;
  }

  /* Pricing tables: horizontal scroll */
  .simple-pricing-wrapper,
  .tab-content,
  .tab-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .product-table {
    min-width: 580px;
  }

  .product-table th,
  .product-table td {
    padding: 8px 5px;
    font-size: 0.8em;
    white-space: nowrap;
  }

  /* Let description column wrap since it has the most text */
  .product-table td:nth-child(2) {
    white-space: normal;
    min-width: 120px;
  }

  .desc-content {
    font-size: 0.8em;
  }

  /* Nav tabs: scroll horizontally if many options */
  .nav-tabs {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .nav-tabs .nav-link {
    white-space: nowrap;
    padding: 8px 12px;
    font-size: 0.85em;
  }

  /* Image grid: stack images full-width on mobile */
  .content-blocks.image-grid-section {
    flex-direction: column;
    align-items: center;
  }

  .content-blocks.image-grid-section :deep(img) {
    max-width: 100% !important;
    width: 100% !important;
  }

  /* Ensure all content block images scale down */
  .proposal-details :deep(img) {
    max-width: 100% !important;
    height: auto !important;
  }

  /* Force any inline-width elements from WYSIWYG to respect viewport */
  .proposal-details :deep([style*="width"]) {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }

  /* Handle iframes in content blocks (e.g. embedded videos) */
  .proposal-details :deep(iframe) {
    max-width: 100% !important;
    height: auto !important;
    aspect-ratio: 16 / 9;
  }

  /* Modal sizing for mobile */
  .modal-content iframe {
    height: 400px !important;
  }
}

/* --- Small Mobile (≤ 480px) --- */
@media (max-width: 480px) {
  .main-content {
    padding: 10px;
  }

  .proposal-header {
    padding: 12px;
  }

  .header-text h1 {
    font-size: 1.05em;
  }

  .product-table th,
  .product-table td {
    padding: 6px 4px;
    font-size: 0.75em;
  }

  .product-table {
    min-width: 520px;
  }

  .badge-included {
    font-size: 0.7em;
    padding: 2px 5px;
  }

  .content-blocks.image-grid-section {
    gap: 10px;
  }

  .proposal-details {
    padding: 12px;
  }

  .right-sidebar {
    padding: 15px 10px;
  }

  .top-actions .button {
    font-size: 0.8em;
    padding: 8px 6px;
    min-width: 80px;
  }
}
</style>